import {
  Component,
  EventEmitter,
  forwardRef,
  OnInit, Output
} from '@angular/core';

import { Observable, forkJoin, of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

import {
  FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR
} from '@angular/forms';
import { Attestation, AttestationDataService } from '@sondermind/data-access/attestation';
import { AccountSetupService } from '@sondermind/utilities/services';
import { BannerMessage } from '@sondermind/core/design/success-error-banner/success-error-banner.component';

@Component({
  selector: 'client-setup-attestation-agree',
  templateUrl: './setup-attestation-agree.component.html',
  styleUrls: ['./setup-attestation-agree.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SetupAttestationAgreeComponent),
      multi: true
    }
  ],
  standalone: false
})
export class SetupAttestationAgreeComponent implements OnInit {
  @Output() attestationsSubmitted: EventEmitter<void> = new EventEmitter<void>();

  // typically accessed by parent component to know when submit is allowed
  // used to prevent user from getting stuck if they are missing an attestation, such as legacy clients
  // PE-14392 fixed most of these but there may still be fringe cases
  missingAttestation: boolean = false;
  missingAttestationText: BannerMessage = {
    title: 'Account setup error',
    description: 'There was an issue setting up your account. Please contact us to resolve it by clicking Support in the main menu.'
  };

  hipaaAttestation: Attestation;
  tosAttestation: Attestation;

  hipaaHeader: string = 'HIPAA';
  tosHeader: string = 'Terms of Service';
  submitting: boolean = false;
  setupForm!: FormGroup;

  constructor(
    private attestationDataService: AttestationDataService,
    private accountSetupService: AccountSetupService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit() {
    this.attestationInit();
    this.setupForm = this.formBuilder.group({
      agreedHipaa: new FormControl(false),
      agreedTos: new FormControl(false)
    });
  }

  /**
   * Get the HIPAA and ToS attestations.
   * NOTE: user will be blocked here if the attestation does not exist on the account,
   * but that would indicate a bad data state that should not exist after PE-14392 is completed.
   */
  attestationInit(): void {
    this.attestationDataService.refresh().subscribe(
      (atts: Attestation[]) => {
        this.hipaaAttestation = atts.find((a) => a.documentTitle.includes(this.hipaaHeader));
        this.tosAttestation = atts.find((a) => a.documentTitle.includes(this.tosHeader));

        if (this.hipaaAttestation == null || this.tosAttestation == null) {
          this.missingAttestation = true;
        }
      }
    );
  }

  /**
   * Filter list of attestations to find HIPAA and Terms of Service, submit them as signed
   */
  signAttestations(): void {
    if (this.setupForm?.value?.agreedHipaa && this.setupForm?.value?.agreedTos) {
      this.startSubmitting();

      this.attestationDataService.refresh()?.pipe(
        switchMap(
          (resp: Attestation[]) => {
            // find HIPAA and ToS
            const filtered: Attestation[] = resp.filter(
              (att) => att.agreedAt == null
              && (att.documentTitle.includes(this.hipaaHeader) || att.documentTitle.includes(this.tosHeader))
            );
            const signRequests: Array<Observable<void>> = filtered.map(
              (att) => this.attestationDataService.markAgreed(att.id, this.accountSetupService.context)
            );

            // if attestations have all been signed, dont forkJoin
            if (signRequests.length === 0) {
              return of(undefined);
            }

            return forkJoin(signRequests);
          }
        ),
        finalize(() => this.stopSubmitting())
      ).subscribe({
        next: () => {
          this.attestationsSubmitted.emit();
        },
        error: (err) => {
          // eslint-disable-next-line no-console
          console.error('Failed to sign attestations.');
          // eslint-disable-next-line no-console
          console.error(err);
        }
      });
    }
  }

  /**
  * Wrapper for better unit tests.
  */
  startSubmitting(): void {
    this.submitting = true;
  }

  /**
  * Wrapper for better unit tests.
  */
  stopSubmitting(): void {
    this.submitting = false;
  }
}
